import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode, type ButtonHTMLAttributes, type InputHTMLAttributes } from 'react';
import { t } from '../i18n';

export function Button({ variant = 'default', size, loading, children, className = '', ...p }: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: 'default' | 'primary' | 'danger' | 'ghost'; size?: 'lg'; loading?: boolean }) {
  return <button type="button" {...p} disabled={p.disabled || loading} aria-busy={loading || undefined}
    className={`btn ${variant !== 'default' ? `btn-${variant}` : ''} ${size === 'lg' ? 'btn-lg' : ''} ${className}`}>{loading ? t('common.loading') : children}</button>;
}

export function Field({ label, error, className = '', ...p }: InputHTMLAttributes<HTMLInputElement> & { label: string; error?: string }) {
  const id = p.id ?? p.name;
  return <div className="field"><label htmlFor={id}>{label}</label>
    <input {...p} id={id} className={`input ${className}`} aria-invalid={!!error} aria-describedby={error ? `${id}-err` : undefined} />
    {error && <span className="err" id={`${id}-err`} role="alert">{error}</span>}</div>;
}

export function Select({ label, value, onChange, options }: { label: string; value: string; onChange: (v: string) => void; options: { value: string; label: string }[] }) {
  const id = 'sel-' + label.replace(/\W+/g, '-').toLowerCase();
  return <div className="field"><label htmlFor={id}>{label}</label>
    <select id={id} className="input" value={value} onChange={e => onChange(e.target.value)}>{options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}</select></div>;
}

export const Card = ({ children, action, className = '' }: { children: ReactNode; action?: boolean; className?: string }) =>
  <section className={`card ${action ? 'card-action' : ''} ${className}`}>{children}</section>;

const TONE: Record<string, string> = { active: 'ok', checkedIn: 'ok', booked: 'info', trial: 'info', lead: 'info', waitlisted: 'warn', frozen: 'info', pastDue: 'danger', noShow: 'danger', cancelPending: 'warn', lateCancel: 'warn' };
export const StatusBadge = ({ status }: { status: string }) => <span className={`badge ${TONE[status] ? `badge-${TONE[status]}` : ''}`}>{t(`status.${status}`)}</span>;
export const Badge = ({ tone, children }: { tone?: 'ok' | 'warn' | 'danger' | 'info'; children: ReactNode }) => <span className={`badge ${tone ? `badge-${tone}` : ''}`}>{children}</span>;

export function Belt({ color, stripes, black, label }: { color: string; stripes: number; black?: boolean; label: string }) {
  return <span className={`belt ${black ? 'black' : ''}`} role="img" aria-label={label} style={{ background: color }}>
    <span className="tip">{Array.from({ length: stripes }, (_, i) => <i key={i} />)}</span></span>;
}

export const Skeleton = ({ lines = 3 }: { lines?: number }) => <div className="stack" aria-busy="true" aria-label={t('common.loading')}>{Array.from({ length: lines }, (_, i) => <div key={i} className="skeleton" />)}</div>;
export const Empty = ({ children, action }: { children: ReactNode; action?: ReactNode }) => <div className="empty"><p>{children}</p>{action}</div>;

export function Tabs<K extends string>({ tabs, value, onChange }: { tabs: { key: K; label: string }[]; value: K; onChange: (k: K) => void }) {
  return <div className="tabs" role="tablist">{tabs.map(x => <button key={x.key} type="button" role="tab" aria-selected={x.key === value} onClick={() => onChange(x.key)}>{x.label}</button>)}</div>;
}

export function Drawer({ open, onClose, title, children }: { open: boolean; onClose: () => void; title: string; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);
  useEffect(() => {
    if (!open) return;
    const k = (e: KeyboardEvent) => { if (e.key === 'Escape') onCloseRef.current(); };
    addEventListener('keydown', k);
    ref.current?.focus();
    return () => removeEventListener('keydown', k);
  }, [open]);
  if (!open) return null;
  return <><div className="drawer-bg" onClick={onClose} /><div className="drawer" role="dialog" aria-modal="true" aria-label={title} tabIndex={-1} ref={ref}>
    <div className="spread"><h2 style={{ margin: 0 }}>{title}</h2><Button variant="ghost" onClick={onClose} aria-label={t('common.close')}>✕</Button></div>{children}</div></>;
}

/** Confirmation for destructive actions. */
export function useConfirm() { return useCallback((msg: string) => Promise.resolve(window.confirm(msg)), []); }

type Toast = { id: number; kind: 'ok' | 'err'; msg: string };
const ToastCtx = createContext<(kind: Toast['kind'], msg: string) => void>(() => {});
export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, set] = useState<Toast[]>([]);
  const push = useCallback((kind: Toast['kind'], msg: string) => { const id = Date.now() + Math.random(); set(x => [...x, { id, kind, msg }]); setTimeout(() => set(x => x.filter(i => i.id !== id)), kind === 'err' ? 7000 : 3500); }, []);
  return <ToastCtx.Provider value={push}>{children}<div className="toasts" role="status" aria-live="polite">{items.map(i => <div key={i.id} className={`toast ${i.kind}`}>{i.msg}</div>)}</div></ToastCtx.Provider>;
}
export const useToast = () => useContext(ToastCtx);

/** Any thrown error → a message that says what happened and what to do. */
export function errorMessage(e: unknown): string {
  if (typeof navigator !== 'undefined' && !navigator.onLine) return t('errors.offline');
  const m = (e as { message?: string })?.message;
  const code = (e as { code?: string })?.code ?? '';
  if (code.startsWith('functions/') && m && code !== 'functions/internal') return m;
  if (code.endsWith('permission-denied')) return t('errors.permission');
  console.error(e);
  return t('errors.generic');
}

export function useOnline() {
  const [on, set] = useState(navigator.onLine);
  useEffect(() => { const a = () => set(true), b = () => set(false); addEventListener('online', a); addEventListener('offline', b); return () => { removeEventListener('online', a); removeEventListener('offline', b); }; }, []);
  return on;
}
